<script setup lang="ts">
import { watch } from 'vue';
import { trackPageView } from '@/api/tracking';
import { DgrLoading } from '@stockmarkteam/donguri-ui';
import Divider from '@/components/common/divider.vue';
import InsightAdjustButton from '@/components/home/insights/insight-adjust-button.vue';
import InsightProfileModal from '@/components/home/insights/insight-profile-modal.vue';
import InsightRegenerateBanner from '@/components/home/insights/insight-regenerate-banner.vue';
import InsightsGenerationError from '@/components/home/insights/insights-generation-error.vue';
import InsightsGenerationProgress from '@/components/home/insights/insights-generation-progress.vue';
import InsightsProfileReview from '@/components/home/insights/insights-profile-review.vue';
import InsightsProfile from '@/components/home/insights/insights-profile.vue';
import InsightsTabs from '@/components/home/insights/insights-tabs.vue';
import { useInsightsContainer } from '@/components/home/insights/use-insights-container';
import { INSIGHTS_HEADER_ACTIONS_ID } from '@/components/insights/insights-header-actions';
import InsightsSample from '@/components/insights/insights-sample.vue';
import { useCurrentPathForTracking } from '@/utils/composables/useCurrentPathForTracking';
import { consumeInsightTodayEntry } from '@/utils/insights/insightTodayEntry';
import { featureFlags } from '@/featureFlags';

const {
  hasLoaded,
  isInsightEnabled,
  hasProfile,
  personalSurveyProfile,
  canAdjustHints,
  profileModalVariant,
  isProfileModalOpen,
  isSavingProfile,
  hasProfileSaveError,
  saveProfile,
  onSuggestionApply,
  onSuggestionRefresh,
  hasSetupPersonalNewsOrFavoriteThemes,
  hasPersonalNewsArticles,
  hasFeaturedArticles,
  payload,
  data,
  isAwaitingFirstAxis,
  isOpen,
  onProfileSaved,
  onRegenerate,
  retry,
  hasGenerationError,
  hasRegenerationError,
  showRegenerateBanner,
  dismiss,
  progressBarWidth,
  durationLabel,
  showProgress,
  isComplete,
  errorTitle,
  errorMessage,
} = useInsightsContainer({ pageName: 'insight_today' });

const isTopicHintPair = featureFlags.ANDEV_10719_INSIGHT_TOPIC_HINT_PAIR;
const isInsightSample = featureFlags.ANDEV_10807_INSIGHT_SAMPLE;

// 「開いていて生成済みなら既読」の監視をそのまま使うため、このタブでは常に開いた状態として扱う。
isOpen.value = true;

const { getCurrentPathForTracking } = useCurrentPathForTracking();
const entryContext = consumeInsightTodayEntry();
let hasTrackedPageView = false;
// 表示中の発想のヒントの id を含めるため、取得結果に id が付くまで page_view を待つ。
// 当日分が未生成だと最初の取得は id: null で返り、直後の生成開始で id が付く。
// 業務情報が未登録のユーザーは取得しないので、読み込み完了の時点で送る。
watch(
  [hasLoaded, isInsightEnabled, data],
  ([loaded, enabled, insight]) => {
    if (hasTrackedPageView || !loaded) return;
    if (enabled && (insight === undefined || insight.id === null)) return;
    hasTrackedPageView = true;
    const insightId = insight?.id;
    void trackPageView({
      pageName: 'insight_today',
      pageUrl: getCurrentPathForTracking(),
      insightContext:
        typeof insightId === 'number'
          ? { insight: { id: insightId } }
          : undefined,
      context: entryContext,
    });
  },
  { immediate: true },
);
</script>

<template>
  <div class="insights-today">
    <Teleport defer :to="`#${INSIGHTS_HEADER_ACTIONS_ID}`">
      <InsightAdjustButton
        v-if="canAdjustHints"
        @click="isProfileModalOpen = true"
      />
    </Teleport>
    <InsightProfileModal
      v-if="personalSurveyProfile"
      v-model:open="isProfileModalOpen"
      :variant="profileModalVariant"
      :profile="personalSurveyProfile"
      :is-submitting="isSavingProfile"
      :has-save-error="hasProfileSaveError"
      @save="saveProfile"
      @suggestion-apply="onSuggestionApply"
      @suggestion-refresh="onSuggestionRefresh"
    />
    <div v-if="!hasLoaded" class="loading">
      <DgrLoading />
    </div>
    <InsightsSample
      v-else-if="!hasProfile && isInsightSample"
      @register="isProfileModalOpen = true"
    />
    <InsightsProfile
      v-else-if="!hasProfile"
      :is-always-open="true"
      layout="split"
      @profile-saved="onProfileSaved"
    />
    <div
      v-else-if="
        showProgress ||
        hasGenerationError ||
        data === undefined ||
        isAwaitingFirstAxis
      "
      class="insights-card"
    >
      <InsightsGenerationProgress
        v-if="showProgress"
        :progress-bar-width="progressBarWidth"
        :duration-label="durationLabel"
        :is-complete="isComplete"
      />
      <InsightsGenerationError
        v-else-if="hasGenerationError"
        :title="errorTitle"
        :message="errorMessage"
        @retry="retry"
      />
      <div v-else class="loading">
        <DgrLoading />
      </div>
    </div>
    <div v-else class="insights-result">
      <InsightsGenerationError
        v-if="hasRegenerationError"
        title="発想のヒントの再生成に失敗しました"
        :message="errorMessage"
        @retry="retry"
      />
      <InsightRegenerateBanner
        v-else-if="showRegenerateBanner"
        @regenerate="onRegenerate"
        @close="dismiss"
      />
      <InsightsTabs
        layout="split"
        :payload="payload"
        :has-setup-personal-news-or-favorite-themes="
          hasSetupPersonalNewsOrFavoriteThemes
        "
        :has-personal-news-articles="hasPersonalNewsArticles"
        :has-featured-articles="hasFeaturedArticles"
      >
        <template #footer>
          <Divider
            v-if="!isTopicHintPair"
            class="full-bleed"
            margin="0 -24px 0 -24px"
          />
          <InsightsProfileReview :class="{ paired: isTopicHintPair }" />
        </template>
      </InsightsTabs>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.insights-today {
  padding-bottom: 24px;
}

.loading {
  display: flex;
  justify-content: center;
  padding: 24px;
}

.insights-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 24px;
  border: 1px solid $color-gray400;
  border-radius: 4px;
  background-color: $color-white;
}

.insights-result {
  display: flex;
  flex-direction: column;
  gap: 16px;

  .regenerate-banner {
    background-color: $color-white;
  }

  .full-bleed {
    width: auto;
  }

  // 区切り線が無くなり、カードの列の 16px 間隔に揃えるため上の余白を消す。
  .profile-info.paired {
    margin-top: 0;
  }
}
</style>
